import { QUERY_KEYS } from '@/constants/query';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSDK } from '../web3/hook';
import {
  getAccountBalanceHistory,
  getAllPairBasicInfoByGraph,
  getFundingHistory,
  getLiquidityHistory,
  getOrdersHistory,
  getTradeHistory,
  getTransferHistory,
} from './api';

export const useAllPairBasicInfoByGraph = (chainId: number | undefined) => {
  const sdk = useSDK(chainId);
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: QUERY_KEYS.GRAPH.ALL_INSTRUMENTS_BASIC_INFO(chainId),
    queryFn: async () => getAllPairBasicInfoByGraph({ chainId, sdk }),
    enabled: chainId !== undefined && sdk !== undefined,
  });
};

export const useInfiniteVirtualTradeHistoryQuery = (args: Omit<Parameters<typeof getTradeHistory>[0], 'page'>) => {
  return useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: QUERY_KEYS.GRAPH.VIRTUAL_TRADE_HISTORY(args),
    queryFn: async ({ pageParam = 1 }) => {
      return getTradeHistory({
        ...args,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.length > 0 ? allPages?.length : undefined;
    },
    enabled: !!args.chainId && !!args.userAddress,
    initialPageParam: 1,
  });
};

export const useInfiniteOrdersHistoryQuery = (args: Omit<Parameters<typeof getOrdersHistory>[0], 'page'>) => {
  return useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: QUERY_KEYS.GRAPH.ORDERS_HISTORY(args),
    queryFn: async ({ pageParam = 1 }) => {
      return getOrdersHistory({
        ...args,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.length > 0 ? allPages?.length : undefined;
    },
    enabled: !!args.chainId && !!args.userAddress,
    initialPageParam: 1,
  });
};

export const useInfiniteFundingHistoryQuery = (args: Omit<Parameters<typeof getFundingHistory>[0], 'page'>) => {
  return useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: QUERY_KEYS.GRAPH.FUNDING_HISTORY(args),
    queryFn: async ({ pageParam = 1 }) => {
      return getFundingHistory({
        ...args,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage, allPages) => (lastPage?.length ? allPages.length : undefined),
    enabled: !!args.chainId && !!args.userAddress,
    initialPageParam: 1,
  });
};

export const useInfiniteTransferHistoryQuery = (args: Omit<Parameters<typeof getTransferHistory>[0], 'page'>) => {
  return useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: QUERY_KEYS.GRAPH.TRANSFERS_HISTORY(args),
    queryFn: async ({ pageParam = 1 }) => {
      return getTransferHistory({
        ...args,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage, allPages) => (lastPage?.length ? allPages.length : undefined),
    enabled: !!args.chainId && !!args.userAddress,
    initialPageParam: 1,
  });
};

export const useInfiniteLiquidityHistoryQuery = (args: Omit<Parameters<typeof getLiquidityHistory>[0], 'page'>) => {
  return useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: QUERY_KEYS.GRAPH.LIQUIDITY_HISTORY(args),
    queryFn: async ({ pageParam = 1 }) => {
      return getLiquidityHistory({
        ...args,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage, allPages) => (lastPage?.length ? allPages.length : undefined),
    enabled: !!args.chainId && !!args.userAddress,
    initialPageParam: 1,
  });
};

export const useInfiniteAccountBalanceHistoryQuery = (
  args: Omit<Parameters<typeof getAccountBalanceHistory>[0], 'page'>,
) => {
  return useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: QUERY_KEYS.GRAPH.ACCOUNT_BALANCE_HISTORY(args),
    queryFn: async ({ pageParam = 1 }) => {
      return getAccountBalanceHistory({
        ...args,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage, allPages) => (lastPage?.length ? allPages.length : undefined),
    enabled: !!args.chainId && !!args.userAddress,
    initialPageParam: 1,
  });
};
